import React from "react";
import { Capacitor } from "@capacitor/core";
import { C } from "../theme";

// Horizontally scrolling "Proudly sponsored by" band for the login screen.
// All active sponsor logos scroll continuously so every sponsor is visible
// (not just the first). Renders nothing when there are no sponsors, so regular
// users see no gap.
//
// The scroll is driven by the Web Animations API (element.animate) measuring
// the real rendered track width via a ResizeObserver — this is far more robust
// inside the Capacitor WebView than an injected @keyframes rule (which was not
// animating), and it recomputes automatically once the logo images load.
//
// Props:
//   sponsors:       [{ imageUrl, linkUrl, name }]
//   mutedColor:     label color (defaults to theme muted)
//   speedPxPerSec:  marquee scroll speed (default 40px/s)
export default function SponsorBand({ sponsors = [], mutedColor = C.muted, speedPxPerSec = 40 }) {
  const count = sponsors.length;
  const trackRef = React.useRef(null);
  const pausedRef = React.useRef(false);
  // Drive the marquee with a requestAnimationFrame loop that writes `transform`
  // directly every frame. This is the most robust approach in the Capacitor
  // WebView: it doesn't depend on injected @keyframes or the Web Animations API,
  // and it re-reads the live width each frame so it self-corrects once the logo
  // images finish loading. We render the sponsor list twice; wrapping the offset
  // at one-copy width loops seamlessly.
  React.useEffect(() => {
    const el = trackRef.current;
    if (!el || count <= 1) return undefined;

    const reduceMotion =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduceMotion) return undefined;

    let raf = 0;
    let offset = 0;
    let last = performance.now();

    const step = (now) => {
      const dt = Math.min(0.05, (now - last) / 1000); // clamp big gaps (tab switches)
      last = now;
      const oneCopy = el.scrollWidth / 2; // width of a single (un-duplicated) copy
      if (oneCopy > 0 && !pausedRef.current) {
        offset = (offset + Math.max(10, speedPxPerSec) * dt) % oneCopy;
        el.style.transform = `translateX(${-offset}px)`;
      }
      raf = requestAnimationFrame(step);
    };

    raf = requestAnimationFrame(step);
    return () => {
      cancelAnimationFrame(raf);
      el.style.transform = "translateX(0)";
    };
  }, [count, speedPxPerSec, sponsors]);

  const openLink = (url) => {
    if (!url) return;
    try {
      // Native: open the system browser; web: a new tab.
      window.open(url, Capacitor?.isNativePlatform?.() ? "_system" : "_blank", "noopener,noreferrer");
    } catch {
      /* ignore */
    }
  };

  if (count === 0) return null;

  const renderLogo = (s, key) => {
    const clickable = Boolean(s.linkUrl);
    return (
      <div
        key={key}
        role={clickable ? "button" : undefined}
        tabIndex={clickable ? 0 : undefined}
        onClick={clickable ? () => openLink(s.linkUrl) : undefined}
        onKeyDown={
          clickable
            ? (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  openLink(s.linkUrl);
                }
              }
            : undefined
        }
        title={clickable ? `Visit ${s.name}` : s.name}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flex: "0 0 auto",
          padding: "0 18px",
          cursor: clickable ? "pointer" : "default",
        }}
      >
        {/* Round the logo image itself: sizing it by height with width:auto makes
            the element box hug the actual artwork, so border-radius clips the
            visible corners (works whether the logo is transparent or has a solid
            rectangular background). */}
        <img
          src={s.imageUrl}
          alt={s.name}
          draggable={false}
          style={{
            height: 60,
            width: "auto",
            maxWidth: "none",
            objectFit: "contain",
            display: "block",
            borderRadius: 10,
          }}
        />
      </div>
    );
  };

  const shell = {
    marginTop: 12,
    border: `1px solid ${C.border}`,
    borderRadius: 14,
    background: "rgba(12,24,44,0.55)",
    padding: "10px 12px 12px",
    textAlign: "center",
  };

  // Single sponsor → centered, static (nothing to scroll).
  if (count === 1) {
    return (
      <div style={shell}>
        <BandLabel color={mutedColor} />
        <div style={{ display: "flex", justifyContent: "center" }}>{renderLogo(sponsors[0], "solo")}</div>
      </div>
    );
  }

  // Multiple sponsors → continuous horizontal marquee (animation applied in the
  // effect above). Pause on hover for readability, but ONLY on hover-capable
  // (desktop) devices. On touch devices the WebView fires a synthetic
  // mouseenter on tap without a matching mouseleave (the tap also opens the
  // sponsor link in the system browser), which would pause the marquee forever.
  const canHover =
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(hover: hover)").matches;
  const hoverHandlers = canHover
    ? {
        onMouseEnter: () => {
          pausedRef.current = true;
        },
        onMouseLeave: () => {
          pausedRef.current = false;
        },
      }
    : null;

  // Share-of-voice: repeat each logo `weight` times (1..3) in the scroll track
  // so higher tiers (Series 3× / Regional 2× / Local 1×) appear proportionally
  // more often. Defaults to 1 when weight is missing.
  const weighted = sponsors.flatMap((s) =>
    Array(Math.max(1, Math.min(3, Number(s.weight) || 1))).fill(s)
  );

  return (
    <div style={shell}>
      <BandLabel color={mutedColor} />

      <div
        {...hoverHandlers}
        style={{
          overflow: "hidden",
          width: "100%",
          maskImage: "linear-gradient(90deg, transparent, #000 8%, #000 92%, transparent)",
          WebkitMaskImage: "linear-gradient(90deg, transparent, #000 8%, #000 92%, transparent)",
        }}
      >
        <div
          ref={trackRef}
          style={{
            display: "flex",
            alignItems: "center",
            width: "max-content",
            willChange: "transform",
          }}
        >
          {weighted.map((s, i) => renderLogo(s, `a-${i}`))}
          {/* Duplicate copy for seamless looping (alt cleared to avoid double reading). */}
          {weighted.map((s, i) => renderLogo({ ...s, name: "" }, `b-${i}`))}
        </div>
      </div>
    </div>
  );
}

function BandLabel({ color }) {
  return (
    <div
      style={{
        fontSize: 9,
        letterSpacing: 1.6,
        textTransform: "uppercase",
        color,
        marginBottom: 8,
      }}
    >
      Proudly sponsored by
    </div>
  );
}
